import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Truck, DollarSign, AlertTriangle, Download, Search, Filter,
  CheckCircle, Clock, Send, Box, ChevronDown, ChevronUp, Loader2, 
  Edit2, Save, X, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import AdminFlaggedCatches from '../components/AdminFlaggedCatches';
import AdminInbox from '../components/AdminInbox';
import AdminFunnel from '../components/AdminFunnel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = [
  'paid - awaiting supplier',
  'order sent to supplier',
  'dispatched',
  'in transit',
  'delivered'
];

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editTrackingNumber, setEditTrackingNumber] = useState('');
  const [editStatus, setEditStatus] = useState('');

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/suppliers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = supplierFilter !== 'all' ? `?supplier=${supplierFilter}` : '';
      window.open(`${API}/admin/orders/export${params}`, '_blank');
      toast.success('CSV export started');
    } catch (error) {
      toast.error('Failed to export orders');
    }
  };

  const handleMarkSentToSupplier = async (orderId) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/mark-sent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order marked as sent to supplier');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleUpdateOrder = async (orderId) => {
    try {
      const updateData = {};
      if (editStatus) updateData.order_status = editStatus;
      if (editTrackingNumber) updateData.tracking_number = editTrackingNumber;

      await axios.put(`${API}/admin/orders/${orderId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated successfully');
      setEditingOrder(null);
      setEditTrackingNumber('');
      setEditStatus('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredOrders = orders.filter(order => {
    const matchesSupplier = supplierFilter === 'all' || 
      order.items?.some(item => item.supplier_name === supplierFilter);
    const matchesStatus = statusFilter === 'all' || order.order_status === statusFilter;
    const matchesSearch = !searchTerm || 
      order.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSupplier && matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050605] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050605] py-8 px-4 md:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-wider text-white">
              Admin Dashboard
            </h1>
            <p className="text-[#8a948a]">Manage orders and products</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => navigate('/admin/competitions')}
              className="btn-gold"
              data-testid="admin-manage-comps-btn"
            >
              Competitions
            </Button>
            <Button
              onClick={() => navigate('/admin/moderation')}
              variant="outline"
              className="border-white/20 text-white"
              data-testid="admin-moderation-link-btn"
            >
              Moderation
            </Button>
            <Button
              onClick={() => navigate('/admin/maintenance')}
              variant="outline"
              className="border-white/20 text-white"
              data-testid="admin-maintenance-link-btn"
            >
              Maintenance
            </Button>
            <Button
              onClick={() => navigate('/admin/audit')}
              variant="outline"
              className="border-white/20 text-white"
              data-testid="admin-audit-link-btn"
            >
              Audit
            </Button>
            <Button
              onClick={() => navigate('/admin/analytics')}
              variant="outline"
              className="border-white/20 text-white"
              data-testid="admin-analytics-link-btn"
            >
              Analytics
            </Button>
            <Button onClick={fetchData} variant="outline" className="border-white/20 text-white">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div 
            className="glass-card p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-[#6b7d6b] text-sm">Total Orders</p>
                <p className="text-white text-2xl font-bold">{stats?.total_orders || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-card p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-[#6b7d6b] text-sm">Pending Orders</p>
                <p className="text-white text-2xl font-bold">{stats?.pending_orders || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-card p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-[#6b7d6b] text-sm">Total Revenue</p>
                <p className="text-white text-2xl font-bold">£{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-card p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#c8b273]/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#c8b273]" />
              </div>
              <div>
                <p className="text-[#6b7d6b] text-sm">Total Profit</p>
                <p className="text-white text-2xl font-bold">£{stats?.total_profit?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Alert for failed emails */}
        {stats?.failed_supplier_emails > 0 && (
          <motion.div 
            className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <div>
              <p className="text-red-400 font-medium">{stats.failed_supplier_emails} orders have failed supplier emails</p>
              <p className="text-red-400/70 text-sm">Please check and manually notify suppliers</p>
            </div>
          </motion.div>
        )}

        {/* Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a504a]" />
              <Input
                placeholder="Search by order ID or customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#0b0d0b] border-white/10 text-white"
              />
            </div>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-full md:w-48 bg-[#0b0d0b] border-white/10 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by supplier" />
              </SelectTrigger>
              <SelectContent className="bg-[#0b0d0b] border-white/10">
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 bg-[#0b0d0b] border-white/10 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0b0d0b] border-white/10">
                <SelectItem value="all">All Statuses</SelectItem>
                {ORDER_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleExportCSV} variant="outline" className="border-white/20 text-white">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="space-y-3">
          {filteredOrders.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Package className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
              <p className="text-[#8a948a]">No orders found</p>
            </div>
          ) : (
            filteredOrders.map((order, index) => {
              const isExpanded = expandedOrder === order.order_id;
              const isEditing = editingOrder === order.order_id;
              const hasFailedEmails = Object.values(order.supplier_status || {}).includes('email failed');

              return (
                <motion.div
                  key={order.order_id}
                  className={`glass-card overflow-hidden ${hasFailedEmails ? 'border border-red-500/50' : ''}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {/* Order Row */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-white/5"
                    onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-[#c8b273] font-mono font-bold">{order.order_id}</p>
                          <p className="text-[#6b7d6b] text-sm">{formatDate(order.created_at)}</p>
                        </div>
                        {hasFailedEmails && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">
                            Email Failed
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-white">{order.customer?.name}</p>
                          <p className="text-[#6b7d6b] text-sm">{order.items?.length} items</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[#c8b273] font-semibold">£{order.total_amount?.toFixed(2)}</p>
                          <p className="text-green-500 text-sm">+£{order.total_profit?.toFixed(2)}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.order_status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                          order.order_status === 'paid - awaiting supplier' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {order.order_status}
                        </span>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-[#6b7d6b]" /> : <ChevronDown className="w-5 h-5 text-[#6b7d6b]" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-white/10 p-4 bg-[#0b0d0b]/50">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Customer Info */}
                        <div>
                          <h4 className="text-white font-medium mb-2">Customer Details</h4>
                          <p className="text-[#8a948a]">{order.customer?.name}</p>
                          <p className="text-[#8a948a]">{order.customer?.email}</p>
                          <p className="text-[#8a948a]">{order.customer?.phone}</p>
                          <p className="text-[#8a948a] whitespace-pre-line mt-2">{order.customer?.shipping_address}</p>
                        </div>

                        {/* Items */}
                        <div>
                          <h4 className="text-white font-medium mb-2">Items</h4>
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="text-sm text-[#8a948a] py-1">
                              {item.sku} - {item.product_name} x{item.quantity} ({item.supplier_name})
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Admin Actions */}
                      <div className="mt-6 pt-4 border-t border-white/10">
                        {isEditing ? (
                          <div className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm text-[#8a948a] mb-1 block">Order Status</label>
                                <Select value={editStatus} onValueChange={setEditStatus}>
                                  <SelectTrigger className="bg-[#0b0d0b] border-white/10 text-white">
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#0b0d0b] border-white/10">
                                    {ORDER_STATUSES.map(s => (
                                      <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-sm text-[#8a948a] mb-1 block">Tracking Number</label>
                                <Input
                                  value={editTrackingNumber}
                                  onChange={(e) => setEditTrackingNumber(e.target.value)}
                                  placeholder="Enter tracking number"
                                  className="bg-[#0b0d0b] border-white/10 text-white"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={() => handleUpdateOrder(order.order_id)} className="btn-primary">
                                <Save className="w-4 h-4 mr-2" />
                                Save Changes
                              </Button>
                              <Button onClick={() => setEditingOrder(null)} variant="outline" className="border-white/20 text-white">
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {order.order_status === 'paid - awaiting supplier' && (
                              <Button 
                                onClick={() => handleMarkSentToSupplier(order.order_id)}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Mark as Sent to Supplier
                              </Button>
                            )}
                            <Button 
                              onClick={() => {
                                setEditingOrder(order.order_id);
                                setEditStatus(order.order_status);
                                setEditTrackingNumber(order.tracking_number || '');
                              }}
                              variant="outline" 
                              className="border-white/20 text-white"
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit Order
                            </Button>
                          </div>
                        )}

                        {/* Payment Reference */}
                        {order.stripe_payment_id && (
                          <p className="text-[#6b7d6b] text-sm mt-4">
                            Stripe Reference: {order.stripe_payment_id}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
        <AdminFlaggedCatches token={token} />
        <AdminInbox token={token} />
        <AdminFunnel token={token} />
      </div>
    </div>
  );
};

export default AdminDashboard;
